import React, { lazy, useEffect, useMemo, useRef, useState } from 'react';
import withSuspense from '../../helpers/withSuspense.jsx';
import ButtonSkeleton from '../../skeleton_loaders/common/ButtonSkeleton.jsx';
import ToggleButtonGroup from '../common/ToggleButtonGroup.jsx';
import queryClient from '../../../query_client.js';
import Button from '../bootstrap/Button.jsx';
import { FaIcon } from '../common/Icons.jsx';
import SkeletonGrid from '../../skeleton_loaders/common/SkeletonGrid.jsx';
import ResourceLayout from './ResourceLayout.jsx';
import { usePersonFilter } from './hooks/usePersonFilter.jsx';

const ProjectResourceSettingsButton = withSuspense(lazy(() => import('./ProjectResourceSettingsButton.jsx')), ButtonSkeleton);
const NewProjectResourceButton = withSuspense(lazy(() => import('./NewProjectResourceButton.jsx')), ButtonSkeleton);
const ResourceGrid = withSuspense(lazy(() => import('./ResourceGrid.jsx')), (props) => <SkeletonGrid gridHeight={props.height} />);

const DEFAULT_HEIGHT = 'calc(100vh - 300px)';

function ProjectResourceMonitoring({
  programmeId,
  projectId,
  permissions = {},
  months,
  viewMode = false,
  initialGroupBy = 'role',
  height = DEFAULT_HEIGHT
}) {
  const personsFilter = permissions.get_own_direct_reports;
  const gridRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [groupBy, setGroupBy] = useState(initialGroupBy);
  const { UserFilterButton, filterModel } = usePersonFilter({ enabled: personsFilter });

  const toggleButtonGroupItems = useMemo(() => {
    return [
      { value: 'role', label: I18n.t('by_role') },
      { value: 'person', label: I18n.t('by_person') }
    ];
  }, [projectId, programmeId]);

  const onSave = () => {
    queryClient.invalidateQueries(['projectResourceList']);
    gridRef.current.api.refreshServerSide?.();
  }

  return (
    <ResourceLayout
      personsFilter={personsFilter}
      permissions={permissions}
      left={<>
        {permissions.create && editMode && <NewProjectResourceButton projectId={projectId} onSave={onSave} />}
        {(permissions.create || permissions.update_cell || permissions.lock_cell || permissions.accept_cell) && 
          <Button type={editMode ? 'default' : 'primary'} onClick={() => setEditMode(prev => !prev)}>
            {!editMode
              ? <><FaIcon type='pencil' /> {I18n.t('edit')}</> 
              : I18n.t('cancel')
            }
          </Button>}
        {UserFilterButton}
        </>}
      center={<>
        {!editMode &&
          <ToggleButtonGroup
            value={groupBy}
            onChange={setGroupBy}
            items={toggleButtonGroupItems}
          />}
        </>}
      right={permissions.update_settings ? <ProjectResourceSettingsButton projectId={projectId} /> : null}
    >
      <ResourceGrid
        {...{
          height,
          projectId,
          programmeId,
          months,
          groupBy,
          permissions,
          editMode,
          viewMode,
          gridRef,
          onSave,
          filterModel,
          personsFilter
        }}
      />
    </ResourceLayout>
  );
}

export default ProjectResourceMonitoring;
